import { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { companyBillingSitesPath, Color, INVOICE_OVERRUN_REFUSAL, Size, asSentence, lineDiscountOf, rederivedDueDate, t, type DividerRow, documentName, editDocumentTitle, newDocumentTitle } from '@bms2/shared';
import { Button, DatePicker, Dropdown, TextInput, Textarea } from '../../components/controls';
import { ItemTable, storedId, dividerDraft, hasUnnamedDivider, newLine, savedRows, withDividers, withGeneratedLines, type ItemLineDraft } from '../../components/composites/ItemTable';
import { ModalField } from '../../components/modals/ModalShell';
import { CustomerNotesBox } from './CustomerNotesBox';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { ShipmentPreferencePicker } from './ShipmentPreferencePicker';
import { SitePicker, useSiteChoices } from './SitePicker';
import { INVOICES_PATH, SALES_ORDERS_PATH, get, post, postingAccountSearch, put, taxCategorySearch, useApi } from './api';
import { useCompanySearch, useItemPicker, usePersonSearch } from '../settings/api';
import { previewLinesOf, useTaxPreview } from './useTaxPreview';
import { useAddNew } from './AddNewHost';
import { useDocumentToday } from '../../shell/ViewerZone';
import { discountFieldsOf, discountLevelOf } from './lineDiscountFields';

/** What the form opens on: the account, its open sites, the terms and a stored Draft's own header. */
export interface InvoiceDefaults {
  companyId: string;
  companyName: string;
  paymentTerms: string;
  sites: { id: string; label: string }[];
  billToSiteId: string | null;
  /** Where the goods went, which the tax is worked out on (piece 25; decision 0644); absent from an older reply. */
  shipToSiteId?: string | null;
  invoiceDate: string;
  dueDate: string;
  isFinal: boolean;
  /** 🔒 How the goods travel (decision 0682); the order's on a raise, and absent from an older reply. */
  shipmentPreferenceId?: string | null;
  /** 🔒 The notes to the customer Region 7 prints (decision 0683); absent from an older reply. */
  customerNotes?: string | null;
  sourceSalesOrderId: string | null;
  sourceSalesOrderNumber: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  /** 🔒 The Discount Scope setting, so the Discount column follows it rather than a literal (decisions 0097, 1031); absent from an older reply. */
  discountLevel?: string;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 🔒 The due date an invoice's date and terms derive, read the way the save
 * re-derives it: from the new pair, unless the stored due date is one the
 * stored pair never derived, which is kept (T114; decision 0681).
 */
export function dueDayOf(answer: Pick<InvoiceDefaults, 'invoiceDate' | 'paymentTerms' | 'dueDate'> | null, invoiceDate: string, terms: string): string {
  if (!answer) return '';
  if (!DAY.test(invoiceDate) || !DAY.test(answer.invoiceDate) || !DAY.test(answer.dueDate)) return answer.dueDate;
  const at = (day: string) => new Date(`${day}T00:00:00Z`);
  const stored = { invoiceDate: at(answer.invoiceDate), paymentTerms: answer.paymentTerms, dueDate: at(answer.dueDate) };
  // The ISO day of a due date, a value for the date field and not display text; the display goes through useDayText (T663).
  return rederivedDueDate(stored, at(invoiceDate), terms.trim() || answer.paymentTerms).toISOString().slice(0, 10);
}

interface OpenOrderRow {
  id: string;
  salesOrderNumber: string;
  status: string;
  total: number;
}

interface UninvoicedLine {
  itemId: string | null;
  /** The Item's own name and SKU, so a line added from an order reads both in the grid (T121; piece 4, the SKU under the name; decision 0303); absent from an older reply. */
  itemName?: string | null;
  sku?: string | null;
  description: string | null;
  quantity: number;
  unitId: string | null;
  unitPriceCents: number | null;
  sourceLineId: string | null;
  discountType: string | null;
  discountValue: number | null;
  /** 🔨 The order line's own category comes with the remainder, so a line the order made exempt is not re-taxed here (decision 0435). */
  taxCategoryId: string | null;
  /** 🔒 The order's group dividers standing directly above this line, so they come with it (decision 0650); absent where none does. */
  dividersAbove?: string[];
}

/** 🔨 A row the grid added carries no stored id; the save posts it as a new line rather than as an edit of one (decision 0445). */

/** The statuses an order is still open at; a Closed or Void order has nothing left to bill. */
const OPEN_ORDER_STATUSES = ['Sent', 'ClientViewed', 'PendingCustomerPO', 'Released'];

/**
 * 🔒 The Invoice's edit mode (decision 0409), on the invoice's own page. The
 * header fields run account, bill-to, invoice date, terms with the derived due
 * date beside them, the number and the final-invoice mark; the lines follow on
 * the Item Table's sales column set, and the sticky footer carries Save as
 * Draft, Save and Issue and Cancel.
 *
 * 🔒 An account with open sales orders shows the banner piece 22 asks for, with
 * Add to invoice pulling the uninvoiced remainder of that order's lines onto
 * this invoice — never the whole line where part is already billed. 🔨 The
 * banner is dismissible for this visit and never permanently.
 */
export function InvoiceForm({ invoiceId = null, apiBaseUrl = '' }: { invoiceId?: string | null; apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const salesOrderFromUrl = params.get('salesOrderId');
  const companyFromUrl = params.get('companyId');
  const [account, setAccount] = useState<{ id: string; label: string } | null>(null);
  const companyId = companyFromUrl || account?.id || '';
  const query = invoiceId
    ? `invoiceId=${encodeURIComponent(invoiceId)}`
    : salesOrderFromUrl
    ? `salesOrderId=${encodeURIComponent(salesOrderFromUrl)}`
    : companyId
    ? `companyId=${encodeURIComponent(companyId)}`
    : null;
  const defaults = useApi<InvoiceDefaults>(query ? `${INVOICES_PATH}/defaults?${query}` : null, apiBaseUrl);
  const answer = defaults.state.kind === 'ready' ? defaults.state.data : null;
  // 🔒 The account's sites, read again once + Add New Site writes one (T185).
  const siteChoices = useSiteChoices(answer, answer ? companyBillingSitesPath(answer.companyId) : null, apiBaseUrl);
  const needsAccount = query === null;
  const asksForAccount = !invoiceId && !salesOrderFromUrl && !companyFromUrl;
  const readFailed = defaults.state.kind === 'failed';
  const stored = useApi<{ lines: { id: string; itemId: string | null; itemName?: string | null; sku: string | null; description: string; quantity: number; unitName: string; unitPriceCents: number | null; sourceLineId: string | null; discountType: string | null; discountValue: number | null; taxCategoryId: string; taxCategoryName: string; taxAmountCents: number; accountId: string; accountLabel: string }[]; dividers?: DividerRow[]; currencyCode: string }>(
    invoiceId ? `${INVOICES_PATH}/${invoiceId}` : null,
    apiBaseUrl,
  );
  // The banner's own read: the account's orders, narrowed to the ones still open (piece 22).
  const orders = useApi<{ rows: OpenOrderRow[] }>(answer && !answer.sourceSalesOrderId ? `${SALES_ORDERS_PATH}?companyId=${encodeURIComponent(answer.companyId)}` : null, apiBaseUrl);

  const [billTo, setBillTo] = useState<string | null>(null);
  const [shipTo, setShipTo] = useState<string | null>(null);
  // 🔒 Opens on today in the viewer's own zone, through the hook every document form reads (decision 1030).
  const today = useDocumentToday();
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [terms, setTerms] = useState('');
  const [shipmentPreference, setShipmentPreference] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  // 🔒 A fresh document opens on one empty line, so the table is typed into rather than found (decision 0447).
  const [drafts, setDrafts] = useState<ItemLineDraft[]>(() => [newLine()]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bannerOff, setBannerOff] = useState(false);
  // 🔒 The over-billing pair, the credit note's bypass shape: opened beneath the lines when a save bills past the order (T918; decision 1703).
  const [overrunAsked, setOverrunAsked] = useState(false);
  const [overrunReason, setOverrunReason] = useState('');
  const [overrunAuthoriser, setOverrunAuthoriser] = useState<{ id: string; label: string } | null>(null);
  // 🔒 A picked Item brings its SKU and its sales description, as the purchase side's lines always did (T121; piece 4, the SKU under the name; decision 0303).
  const sources = useRef(new Map<string, string>());

  const [seen, setSeen] = useState<InvoiceDefaults | null>(null);
  if (answer && seen !== answer) {
    setSeen(answer);
    setBillTo(answer.billToSiteId);
    setShipTo(answer.shipToSiteId ?? null);
    setInvoiceDate(invoiceId ? answer.invoiceDate : today);
    setTerms(answer.paymentTerms);
    setShipmentPreference(answer.shipmentPreferenceId ?? '');
    setCustomerNotes(answer.customerNotes ?? '');
  }

  // 🔒 The due date the save will write, worked out as the date and the terms are typed (T114; decision 0681).
  const dueDay = dueDayOf(answer, invoiceDate, terms);

  const rows = stored.state.kind === 'ready' ? stored.state.data.lines : null;
  const storedDividers = stored.state.kind === 'ready' ? stored.state.data.dividers : null;
  const [seenLines, setSeenLines] = useState<unknown>(null);
  if (rows && seenLines !== rows) {
    setSeenLines(rows);
    // 🔨 The stored category reopens with the line; a rewrite without it re-defaults the line to the Item's (decision 0435).
    // 🔒 A stored line reopens on its Item's own name, never on its description (T118; decision 0640).
    // 🔒 The stored group dividers stand among the lines where they were saved (decision 0650).
    // 🔒 A stored line reopens on its own discount, drawn in the Discount column under Line and sent back either way (decision 1031).
    setDrafts(withDividers(rows.map((row) => ({ id: row.id, itemId: row.itemId, itemLabel: row.itemName ?? row.description, sku: row.sku, description: row.description, qty: row.quantity, unit: row.unitName, rateCents: row.unitPriceCents, accountId: row.accountId, accountLabel: row.accountLabel, taxCategoryId: row.taxCategoryId, taxLabel: row.taxCategoryName, freeText: row.itemId === null, discount: lineDiscountOf(row.discountType, row.discountValue) })), storedDividers));
  }
  const accountSearch = useCompanySearch(apiBaseUrl, 'customer', { activeOnly: true });
  const personSearch = usePersonSearch(apiBaseUrl);
  const { unitIds, pickerProps } = useItemPicker(apiBaseUrl, 'sales');

  const taxSearch = useMemo(() => taxCategorySearch(apiBaseUrl), [apiBaseUrl]);
  // 🔒 The GL account a line posts to; the picker above it is the customer Account and the two are never one control (decision 0435).
  const lineAccountSearch = useMemo(() => postingAccountSearch(apiBaseUrl), [apiBaseUrl]);

  /** 🔨 Add to invoice adds the uninvoiced remainder alone, never the whole line where part is already billed. */
  const addFromOrder = async (orderId: string) => {
    setBusy(true);
    const answered = await get<{ rows: UninvoicedLine[] }>(`${INVOICES_PATH}/uninvoiced/${orderId}`, apiBaseUrl);
    setBusy(false);
    if (!answered.ok) return setError(answered.message);
    if (answered.data.rows.length === 0) return setNotice(t('invoice.openOrders.nothingLeft'));
    const incoming: ItemLineDraft[] = answered.data.rows.flatMap((row, i) => {
        const key = `added-${orderId}-${i}-${row.sourceLineId ?? ''}`;
        if (row.sourceLineId) sources.current.set(key, row.sourceLineId);
        if (row.unitId) unitIds.current.set(key, row.unitId);
        // 🔒 The order's group dividers come with the lines they group, as named bands above them (decision 0650).
        const bands = (row.dividersAbove ?? []).map((name) => dividerDraft({ name }));
        return [...bands, { id: key, itemId: row.itemId, itemLabel: row.itemName ?? row.description ?? '', sku: row.sku ?? null, description: row.description ?? '', qty: row.quantity, unit: '', rateCents: row.unitPriceCents, taxCategoryId: row.taxCategoryId, freeText: row.itemId === null,
          // 🔨 The order line's discount rides with the remainder on the line itself; billing it at the list price is a price nobody agreed to (decision 1031).
          discount: lineDiscountOf(row.discountType, row.discountValue) }];
    });
    setDrafts((held) => withGeneratedLines(held, incoming));
    setNotice(t('invoice.openOrders.added'));
  };

  // 🔒 The lines the save keeps, and the group dividers at their places among them (decision 0650).
  const saveRows = () =>
    savedRows(
      drafts,
      (d) => Boolean(d.itemId || d.itemLabel.trim() || d.description.trim()) && (d.qty ?? 0) > 0,
      (d) => {
        const stored = rows?.find((row) => row.id === d.id);
        return {
          id: storedId(d),
          itemId: d.itemId,
          description: (d.freeText ? d.itemLabel : d.description).trim() || null,
          quantity: d.qty ?? 1,
          // The unit the grid's closed list wrote wins over the Item's own, which is what an override means (decision 0439).
          unitId: d.unitId ?? unitIds.current.get(d.id) ?? (d.itemId ? unitIds.current.get(d.itemId) ?? null : null),
          unitPriceCents: d.rateCents,
          // 🔨 A stored line keeps the Sales Order line it already bills, and an added one keeps the line the remainder came from; per-line invoiced state reads that pair.
          sourceLineId: sources.current.get(d.id) ?? stored?.sourceLineId ?? null,
          // 🔨 An edit rewrites the whole line list, so the discount the line carries is sent back with it; a line saved without it is a line billed at the list price.
          ...discountFieldsOf(d.discount),
          // 🔨 The category and the account travel; the tax amount never does, because the server re-derives it over the resolved lines (decision 0435).
          taxCategoryId: d.taxCategoryId ?? stored?.taxCategoryId ?? null,
          accountId: d.accountId ?? stored?.accountId ?? null,
        };
      },
    );

  // 🔒 The tax is worked out live, through the call the save makes, on every change to a line, the Ship-To or the date (decision 0644).
  const preview = useTaxPreview(
    answer
      ? {
          documentType: 'Invoice', documentId: invoiceId, salesOrderId: answer.sourceSalesOrderId, shipToSiteId: shipTo || null, documentDate: invoiceDate || null,
          lines: previewLinesOf(drafts, (d) => ({ taxCategoryId: d.taxCategoryId ?? rows?.find((row) => row.id === d.id)?.taxCategoryId ?? null, ...discountFieldsOf(d.discount) })),
        }
      : null,
    apiBaseUrl,
  );

  const save = async (then: 'view' | 'issue'): Promise<void> => {
    if (needsAccount) return setError(t('invoice.new.chooseAccount'));
    if (!answer) return setError(t('invoice.new.readFailed'));
    if (!billTo) return setError(t('invoice.new.needsBillTo'));
    if (hasUnnamedDivider(drafts)) return setError(t('item.dividerNeedsName'));
    const { lines, dividers } = saveRows();
    if (lines.length === 0) return setError(t('invoice.new.needsLines'));
    setBusy(true);
    const body = {
      companyId: answer.companyId, sourceSalesOrderId: answer.sourceSalesOrderId, billToSiteId: billTo, shipToSiteId: shipTo || null, invoiceDate: invoiceDate || null, paymentTerms: terms || null, shipmentPreferenceId: shipmentPreference || null, customerNotes, lines, dividers,
      // 🔒 The pair travels once it is asked for, so the save that follows the refusal stands on it (T918).
      ...(overrunAsked ? { overrunReason: overrunReason.trim() || null, overrunAuthorisedByPersonId: overrunAuthoriser?.id ?? null } : {}),
    };
    const saved = invoiceId
      ? await put<{ id: string }>(`${INVOICES_PATH}/${invoiceId}`, body, apiBaseUrl)
      : await post<{ id: string }>(INVOICES_PATH, body, apiBaseUrl);
    setBusy(false);
    // 🔨 The refusal reaches the browser shaped as a sentence by the API's refusal filter, so both sides are compared in that shape (T918).
    if (!saved.ok && asSentence(saved.message) === asSentence(INVOICE_OVERRUN_REFUSAL)) {
      // 🔨 The refusal names a pair the form had no field for; it now opens them beneath the lines and says so (T918).
      setOverrunAsked(true);
      return setError(t('invoice.overrun.asked'));
    }
    if (!saved.ok) return setError(saved.message);
    // 🔨 Save and Issue saves first and opens the issue on the saved invoice, so an issue that fails leaves a saved draft.
    navigate(`/invoices/${invoiceId ?? saved.data.id}${then === 'issue' ? '?act=issue' : ''}`);
  };

  const openOrders = (orders.state.kind === 'ready' ? orders.state.data.rows : []).filter((row) => OPEN_ORDER_STATUSES.includes(row.status));

  return (
    <>
    <DocumentEditPage
      title={answer?.invoiceNumber ?? (invoiceId ? editDocumentTitle('Invoice') : newDocumentTitle('Invoice'))}
      subtitle={answer?.companyName ?? account?.label ?? documentName('Invoice')}
      outwardLabel={t('invoice.action.issue')}
      busy={busy}
      error={error ?? (readFailed ? t('invoice.new.readFailed') : null)}
      onDismissError={() => setError(null)}
      onSaveDraft={() => void save('view')}
      onSaveAndSend={() => void save('issue')}
      onCancel={() => navigate(invoiceId ? `/invoices/${invoiceId}` : '/invoices')}
      header={
        <>
          {asksForAccount ? (
            <div data-invoice-field="account" data-invoice-account={answer?.companyId ?? ''}>
              <Dropdown size={Size.Medium} label={t('invoice.new.account')} entityLabel={t('invoice.new.accountEntity')} search={accountSearch} value={account?.label ?? null} onSelect={(row) => setAccount({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'customer' }, (row) => setAccount({ id: row.id, label: row.line1 }))} required />
            </div>
          ) : (
            <ModalField label={t('invoice.new.account')}>
              <span data-invoice-account={answer?.companyId ?? ''}>{answer?.companyName ?? account?.label ?? ''}</span>
            </ModalField>
          )}
          {answer?.sourceSalesOrderNumber ? (
            <ModalField label={t('invoice.form.salesOrder')}>
              <span data-invoice-sales-order={answer.sourceSalesOrderId ?? ''}>{answer.sourceSalesOrderNumber}</span>
            </ModalField>
          ) : null}
          <div className={styles.sitePicker}>
            <SitePicker picker="billTo" label={t('invoice.new.billTo')} value={billTo} onChange={setBillTo} sites={siteChoices.sites} companyId={answer?.companyId ?? null} companyName={answer?.companyName ?? null} apiBaseUrl={apiBaseUrl} required onCreated={siteChoices.added(setBillTo)} data={{ 'data-invoice-site': 'billTo' }} />
            {answer && siteChoices.sites.length === 0 ? <p className={styles.hint} data-invoice-no-sites="true">{t('invoice.new.noSites')}</p> : null}
          </div>
          <div className={styles.sitePicker}>
            {/* 🔒 Where the goods went is what the invoice is taxed on, so an invoice raised without an order names it here (piece 25; decision 0644). */}
            <SitePicker picker="shipTo" label={t('invoice.new.shipTo')} value={shipTo} onChange={setShipTo} sites={siteChoices.sites} companyId={answer?.companyId ?? null} companyName={answer?.companyName ?? null} apiBaseUrl={apiBaseUrl} placeholder={t('invoice.new.notChosen')} onCreated={siteChoices.added(setShipTo)} data={{ 'data-invoice-site': 'shipTo' }} />
          </div>
          <DatePicker size={Size.Small} label={t('invoice.new.invoiceDate')} value={invoiceDate} onChange={setInvoiceDate} mode="date" name="invoiceDate" />
          <TextInput size={Size.Large} label={t('invoice.new.paymentTerms')} value={terms} onChange={setTerms} name="invoicePaymentTerms" />
          <ModalField label={t('invoice.new.dueDate')}>
            {/* 🔨 The due date is derived from the document's own date and terms and is never typed; it follows both as they are typed, the way the save re-derives it (T114; decision 0681). */}
            <span data-invoice-due={dueDay}>{dueDay}</span>
          </ModalField>
          <ShipmentPreferencePicker value={shipmentPreference} onChange={setShipmentPreference} apiBaseUrl={apiBaseUrl} name="invoice" />
          <ModalField label={t('invoice.form.number')}>
            <span data-invoice-field="number">{answer?.invoiceNumber ?? t('invoice.form.numberOnIssue')}</span>
          </ModalField>
          {notice ? <p className={styles.hint} data-invoice-notice="true">{notice}</p> : null}
          {/* 🔒 The Open Sales Orders banner, dismissible for this visit and never permanently (piece 22). */}
          {!bannerOff && openOrders.length > 0 ? (
            <div className={styles.wide} data-invoice-open-orders={openOrders.length}>
              <ModalField label={t('invoice.openOrders.title')}>
                <span className={styles.choice}>
                  {openOrders.map((row) => (
                    <Button key={row.id} size={Size.Small} color={Color.Navy} text={[`${row.salesOrderNumber} · ${t('invoice.openOrders.add')}`]} actions={[() => void addFromOrder(row.id)]} disabled={busy} />
                  ))}
                  <Button size={Size.Small} color={Color.Inactive} text={[t('invoice.openOrders.dismiss')]} actions={[() => setBannerOff(true)]} />
                </span>
              </ModalField>
            </div>
          ) : null}
          <CustomerNotesBox value={customerNotes} onChange={setCustomerNotes} name="invoice" />
        </>
      }
      lines={
        <>
        <ItemTable
          documentType="Invoice"
          columns={['item', 'qty', 'unit', 'rate', 'account', 'tax', 'amount']}
          lines={drafts}
          onChange={setDrafts}
          // 🔒 The Discount column follows the Discount Scope setting the opening read names, as the Quote's does (decisions 0651, 1031).
          discountLevel={discountLevelOf(answer?.discountLevel)}
          {...pickerProps({ saleGuards: true })}
          accountSearch={lineAccountSearch}
          taxSearch={taxSearch}
          // 🔨 The figure under the category is the calculation's own, worked out live and never typed (decisions 0435, 0644).
          taxAmountCentsOf={(draft) => preview.taxCentsOf(draft.id) ?? rows?.find((row) => row.id === draft.id)?.taxAmountCents ?? null}
          totals={preview}
        />
        {/* 🔒 Billing past the order asks why and who allowed it, beneath the lines, as the credit note's bypass does; shipping charges never ask (T918; decision 1703). */}
        {overrunAsked ? (
          <div className={styles.fields} data-invoice-overrun="true">
            <ModalField label={t('invoice.overrun.reason')}>
              <Textarea size={Size.Large} label={t('invoice.overrun.reason')} labelHidden value={overrunReason} onChange={setOverrunReason} name="overrunReason" />
            </ModalField>
            <Dropdown size={Size.Medium} label={t('invoice.overrun.authoriser')} entityLabel={t('invoice.overrun.authoriserEntity')} search={personSearch} value={overrunAuthoriser?.label ?? null} onSelect={(row) => setOverrunAuthoriser({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'employee' }, (row) => setOverrunAuthoriser({ id: row.id, label: row.line1 }))} />
          </div>
        ) : null}
        </>
      }
    />
    {addNew.host}
    </>
  );
}
